import { ReactNode } from 'react';
import { UnknownMachineConfig } from 'xstate';

import { StateMachineContext } from '../context';
import { useFlowMachine } from '../hooks';
import useFlowRouteGuard from '../hooks/useFlowRouteGuard';
import useGetMachineConfig from '../hooks/useGetMachineConfig';

const WrapperWithMachine = ({
    children,
    machineConfig,
}: {
    children: ReactNode;
    machineConfig: UnknownMachineConfig;
}) => {
    const {
        machine: [state, send, actorRef],
    } = useFlowMachine(machineConfig);
    return (
        <StateMachineContext.Provider value={{ flowState: state, send, actorRef }}>
            {children}
        </StateMachineContext.Provider>
    );
};

const StateMachineWrapper = ({ children }: { children: ReactNode }) => {
    const { isBlocked } = useFlowRouteGuard();
    const machineConfig = useGetMachineConfig();

    if (isBlocked) {
        // A flow-only page was entered without the matching flow active in session.
        // `useFlowRouteGuard` has already dispatched `router.replace('/')`; render
        // nothing while that redirect is in flight. Note: because the guard flips
        // `isBlocked` inside `useEffect`, children mount briefly on the first render
        // before this branch runs — acceptable here since real auth is enforced
        // server-side and this guard is UX defense-in-depth.
        return null;
    }

    if (!machineConfig || !machineConfig.id) {
        return <>{children}</>;
    }

    // Key on machineConfig.id so React unmounts the wrapper (and its useMachine) when the active
    // flow changes (e.g. ptvSisu -> account after sign-in). Without this, @xstate/react carries
    // the previous flow's persisted snapshot into the new machine via getPersistedSnapshot, which
    // either lands the new actor straight into a stopped final state or throws if the snapshot's
    // state value doesn't exist on the new machine.
    return (
        <WrapperWithMachine key={machineConfig.id as string} machineConfig={machineConfig}>
            {children}
        </WrapperWithMachine>
    );
};

export default StateMachineWrapper;
